import React, { useEffect, useState } from 'react';
import { translate } from '../../Services/translation';
import { useBCApi } from './hooks/BCApi';
import { BCFormInput } from './BCFormInput';
import constants from '../../constants';
import { string, object } from 'yup';
import { setErrors } from './helpers/errors';

const userValidationFields = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
};

export const BCUserRow = ({
  user,
  fetch,
  onDelete,
  createForm,
  createRequest,
  isOrderApprover,
}) => {
  const [editing, setEditing] = useState(createForm);
  const [userForm, setUserForm] = useState({});
  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
  });

  const { value, error, status, execute } = useBCApi(
    `/api/bcmypages/users`,
    false
  );

  const userSchema = object().shape({
    firstName: string().required(translate(`validation.required`)),
    lastName: string().required(translate(`validation.required`)),
    email: string()
      .email(translate(`validation.email`))
      .required(translate(`validation.required`)),
    phone: string().required(translate(`validation.required`)),
    role: string().required(translate(`validation.required`)),
  });

  const onChange = (e) => {
    setUserForm({ ...userForm, [e.target.name]: e.target.value });
  };

  const submitForm = async () => {
    var result = await userSchema
      .validate(userForm, { abortEarly: false })
      .catch((err) => {
        const errors = err.inner.reduce((acc, error) => {
          return {
            ...acc,
            [error.path]: error.message,
          };
        }, {});

        setValidationErrors(() => {
          const newErrors = {};
          Object.keys(errors).forEach((key) => {
            newErrors[key] = errors[key] || '';
          });
          return newErrors;
        });

        return false;
      });

    if (result === false) return;

    setValidationErrors(
      Object.keys(validationErrors).reduce((acc, key) => {
        return {
          ...acc,
          [key]: '',
        };
      }, {})
    );

    if (createForm) {
      createRequest.execute('POST', userForm);
    } else {
      execute('PUT', userForm);
    }
  };

  useEffect(() => {
    if (user) {
      setUserForm(user);
    }
  }, [user]);

  useEffect(() => {
    if (status === 'success') {
      fetch();
      setValidationErrors(userValidationFields);
    }
  }, [status, fetch]);

  useEffect(() => {
    if (error && status == 'error') {
      var errors = setErrors(error, validationErrors);
      setValidationErrors({ ...errors });
    }
  }, [error, status]);

  useEffect(() => {
    if (createRequest?.error && createRequest?.status == 'error') {
      var errors = setErrors(createRequest.error, validationErrors);
      setValidationErrors(() => ({
        ...errors,
      }));
    }
  }, [createRequest?.error, createRequest?.status]);

  const name = [user.firstName, user.lastName]
      .filter(x => x !== null || x !== '')
      .join(' ');

  return (
    <div className="BCUserRow">
      <div className="BCUserRow__head">
        <div className="BCUserRow__item">
          <i className="BCUserRow__item__user-icon" />
          {!editing
            ? `${name}`
            : createForm
            ? translate('business.users.newuser')
            : translate('business.users.edituser')}
        </div>
        <div className="BCUserRow__item">{!editing && user.email}</div>
        <div className="BCUserRow__item">
          {!editing && translate(`business.users.role.${user.role}`)}
        </div>
        {user.editable && isOrderApprover && (
          <div className="BCUserRow__item align-right">
            {!createForm && (
              <div
                className="BCUserRow__item__icon-button"
                onClick={() => setEditing(!editing)}
              >
                <i className="BCUserRow__item__edit-icon" />
              </div>
            )}
            <div
              className="BCUserRow__item__icon-button"
              onClick={() => onDelete()}
            >
              <i className="BCUserRow__item__delete-icon" />
            </div>
          </div>
        )}
      </div>
      {editing && (
        <div className="BCUserRow__body">
          <div className="BCUserRow__body__row">
            <BCFormInput
              onChange={onChange}
              name="firstName"
              value={userForm.firstName}
              label={translate('business.users.firstname')}
              error={validationErrors.firstName}
            />
            <BCFormInput
              onChange={onChange}
              name="lastName"
              value={userForm.lastName}
              label={translate('business.users.lastname')}
              error={validationErrors.lastName}
            />
          </div>
          <div className="BCUserRow__body__row">
            <BCFormInput
              onChange={onChange}
              name="email"
              value={userForm.email}
              label={translate('business.users.email')}
              error={validationErrors.email}
            />
            <BCFormInput
              onChange={onChange}
              name="phone"
              value={userForm.phone}
              label={translate('business.users.phone')}
              error={validationErrors.phone}
            />
          </div>
          <div className="BCUserRow__body__row">
            <div className="BCUserRow__body__select">
              <label className="BCFormInput__label">
                {translate('business.users.role')}
              </label>
              <select
                className="form__input"
                value={userForm.role || ''}
                onChange={onChange}
                name="role"
              >
                {Object.keys(constants.role).map((role) => (
                  <option key={role} value={constants.role[role]}>
                    {translate(
                      'business.' + constants.role[role].toLowerCase()
                    )}
                  </option>
                ))}
              </select>
            </div>
            <div className="BCUserRow__body__save">
              {createForm && <p>{translate('business.users.adduserinfo')}</p>}
              <button
                className="BCUserRow__body__savebutton"
                onClick={() => submitForm()}
              >
                {translate('business.users.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
